import FormTextarea from '@c/forms/controls/FormTextarea';
import { MessageIcon } from '@c/icons';
import ProductInfo from '@c/products/ProductInfo';
import { useQuery } from '@tanstack/react-query';
import Avatar from '@ui/Avatar';
import Button from '@ui/Button';
import {
  createChat,
  allocateChatId,
  sendMessageToChat,
} from '@util/firestore/messages/messages.service';
import {
  detectForSender,
  sendContactInfoSlackAlert,
  addSafetyWarningToChat,
} from '@util/firestore/messages/contact-info-detection';
import {
  checkMessageRateLimit,
  recordMessageInitiation,
  sendRateLimitAlerts,
  getRateLimitMessage,
  SUPPORT_ID,
} from '@util/firestore/messages/rate-limit';
import { getProductById } from '@util/firestore/products';
import { getUserDocument } from '@util/firestore/users';
import useRealtimeChats from '@util/hooks/useRealtimeChats';
import { formatPhoneNumber, isMobile } from '@util/index';
import {
  ChatDocument,
  ChatDocumentSchema,
  ChatMessagesDocumentSchema,
} from '@util/types/firestore/chat';
import { OrderDocument } from '@util/types/firestore/order';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import { useRouter } from 'next/router';
import React from 'react';
import BaseModal from './BaseModal';
import { MessagingPausedError } from '@util/firestore/messages/messaging-pause';
import { requestChatMaskProof } from '@util/firestore/messages/mask-proof';

interface ChatModalProps {
  isOpen: boolean;
  dismiss: () => void;
  recipientUid: string;
  productId?: string;
  order?: OrderDocument;
}

function ChatModal({
  isOpen,
  dismiss,
  recipientUid,
  productId,
  order,
}: ChatModalProps) {
  const { data: recipient, isLoading } = useQuery(['user', recipientUid], () =>
    getUserDocument({ uid: recipientUid })
  );
  const { data: product } = useQuery(
    ['product', productId],
    () => getProductById(productId!),
    {
      enabled: !!productId,
    }
  );
  const { userDoc } = useAuth();
  const { realtimeChats } = useRealtimeChats(userDoc?.uid);

  const [message, setMessage] = React.useState('');
  const [isSending, setIsSending] = React.useState(false);
  const [rateLimitError, setRateLimitError] = React.useState<{
    title: string;
    message: string;
  } | null>(null);
  const { openChatDrawer } = useChat();
  const router = useRouter();

  const getSellerId = () => {
    // if productid, check if user is seller or buyer
    if (productId) {
      return product?.seller_id === userDoc?.uid ? userDoc?.uid : recipientUid;
    }
    // if orderid, check if user is seller or buyer
    if (order) {
      return order.buyer_id === userDoc?.uid ? recipientUid : userDoc?.uid;
    }
    // if no productid or orderid, just return recipient id
    return recipientUid;
  };

  async function createAndSendMessage() {
    if (!userDoc) {
      router.push('/login');
      return;
    }
    if (!message) return;

    // Clear any previous rate limit error
    setRateLimitError(null);

    const messageDoc = ChatMessagesDocumentSchema.parse({
      uid: userDoc?.uid ?? '',
      content: message,
      created_at: Date.now(),
    });

    // Two cases: chat exists or chat doesn't exist
    // If chat exists, just send the message (no rate limit for replies)
    const existingRegularChat = realtimeChats?.find(
      (chat) => chat.uids?.includes(recipientUid) && !productId && !order
    );
    const existingProductChat = realtimeChats?.find(
      (chat) => chat.product_id && chat.product_id === productId
    );
    const existingOrderChat = realtimeChats?.find(
      (chat) =>
        chat.order_id &&
        chat.order_id === order?.id &&
        chat.seller_id !== SUPPORT_ID &&
        chat.buyer_id !== SUPPORT_ID
    );

    const existingChat =
      existingRegularChat || existingProductChat || existingOrderChat;

    let chatToOpen = existingChat;
    if (existingChat) {
      // Existing chat - no rate limit for replies
      const participantNames = [
        userDoc?.username || userDoc?.name,
        recipient?.username || recipient?.name,
      ].filter(Boolean) as string[];
      await sendMessageToChat(
        messageDoc,
        existingChat,
        userDoc?.uid,
        participantNames,
        userDoc?.roles
      );
    } else {
      // New conversation - check rate limits
      const rateLimitResult = checkMessageRateLimit(userDoc, recipientUid);

      if (!rateLimitResult.allowed) {
        // User is blocked by rate limit - show friendly message
        const errorMsg = getRateLimitMessage(
          rateLimitResult.reason as 'hourly' | 'daily'
        );
        setRateLimitError(errorMsg);

        // Still send alert if threshold exceeded (even though blocked)
        if (rateLimitResult.shouldAlert) {
          sendRateLimitAlerts(
            userDoc,
            rateLimitResult.initiationsLastHour ?? 0
          );
        }
        return;
      }

      // If chat doesn't exist, create the chat with the message
      const getBuyerId = () => {
        // if productid, check if user is seller or buyer
        if (productId) {
          return product?.seller_id === userDoc?.uid
            ? recipientUid
            : userDoc?.uid;
        }
        // if orderid, check if user is seller or buyer
        if (order) {
          return order.buyer_id === userDoc?.uid ? userDoc?.uid : recipientUid;
        }
        // if no productid or orderid, just return user id
        return userDoc?.uid;
      };

      // Detect and redact contact info. GB #1942: admin/support/trusted-partner
      // senders are exempt (roles come from the signed-in user's own doc; the
      // server is the source of truth and re-checks, so this cannot grant bypass).
      const detectionResult = detectForSender(messageDoc.content, userDoc?.roles);
      const chatId = allocateChatId();
      const proofId = await requestChatMaskProof({
        chatId, senderUid: userDoc.uid, rawContent: messageDoc.content,
        wasRedacted: detectionResult.wasRedacted,
      });
      if (proofId) messageDoc.mask_proof_id = proofId;
      messageDoc.content = detectionResult.redactedContent;
      messageDoc.had_nonallowlisted_link = detectionResult.detectedTypes.includes('suspicious_url');
      messageDoc.had_fraud_mask = detectionResult.wasRedacted;
      messageDoc.fraud_mask_types = detectionResult.wasRedacted
        ? detectionResult.detectedTypes.filter(
            (type) => type !== 'social_platform' && type !== 'offplatform_mention'
          )
        : [];

      const chatDoc: ChatDocument = ChatDocumentSchema.parse({
        id: chatId,
        uids: [userDoc?.uid ?? '', recipientUid],
        buyer_id: getBuyerId() ?? '',
        seller_id: getSellerId() ?? '',
        order_id: order?.id ?? '',
        messages: [messageDoc],
        product_id: productId ?? '',
        created_at: Date.now(),
        unread: {
          [recipientUid]: true,
          [userDoc?.uid ?? '']: false,
        },
        // Flag chat if contact info was detected
        flagged: detectionResult.wasRedacted ? true : undefined,
      });
      if (userDoc?.uid && userDoc.uid !== process.env.STAGING_ID) {
        chatDoc.unread = { [recipientUid]: true, [userDoc.uid]: false };
      }

      await createChat(chatDoc);
      chatToOpen = chatDoc;

      // If suspicious content was detected, send alerts
      if (detectionResult.shouldAlert && chatDoc.id) {
        // Always send Slack alert
        sendContactInfoSlackAlert(
          chatDoc,
          userDoc.uid,
          detectionResult.originalContent,
          detectionResult.detectedTypes,
          [
            userDoc.username || userDoc.name,
            recipient?.username || recipient?.name,
          ].filter(Boolean) as string[]
        ).catch(console.error);

        // Only add safety warning if content was actually redacted
        if (detectionResult.wasRedacted && detectionResult.warningType) {
          addSafetyWarningToChat(chatDoc, detectionResult.warningType).catch(console.error);
        }
      }

      // Record this initiation for rate limiting (don't await - fire and forget)
      recordMessageInitiation(userDoc.uid, recipientUid);

      // Send alerts if user exceeded threshold (even if allowed)
      if (rateLimitResult.shouldAlert) {
        // Refresh userDoc to get updated initiations count, then alert
        sendRateLimitAlerts(userDoc, rateLimitResult.initiationsLastHour ?? 0);
      }
    }
    dismiss();

    if (isMobile()) router.push('/dashboard/messages');
    else if (chatToOpen) openChatDrawer(chatToOpen);
  }

  async function handleSendClick() {
    if (isSending) return;
    setIsSending(true);
    try {
      await createAndSendMessage();
    } catch (error) {
      setRateLimitError({
        title: 'Message not sent',
        message: error instanceof MessagingPausedError
          ? error.message
          : 'Please try again. If this continues, contact support.',
      });
    } finally {
      setIsSending(false);
    }
  }

  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={dismiss}
      width="40rem"
      title={
        <h2 className="flex items-center gap-[1.6rem] text-[2.4rem] font-semibold leading-[3.2rem] text-brand-secondary">
          <MessageIcon color="#242E5F" width={32} height={32} />
          Send Message <br></br>
        </h2>
      }
    >
      <div className="flex w-full max-w-[36rem] flex-col gap-[2.4rem] rounded-[1rem]">
        <div className="flex flex-col  gap-[0.8rem]">
          <h3 className="flex items-center gap-[1.6rem] text-[1.6rem] font-semibold leading-[2.4rem] text-brand-light-black">
            <span>To: </span>
            {isLoading ? (
              '...'
            ) : (
              <Button
                leadingIcon={<Avatar user={recipient!} size="small" />}
                type="text"
                text={recipient?.username}
                noXPadding
                href={`/shop-seller/${recipient?.username_slug}`}
                height="extraSmall"
              />
            )}
          </h3>
          {recipient &&
            getSellerId() === recipient.uid &&
            recipient.uid !== process.env.NEXT_PUBLIC_SUPPORT_ID &&
            !recipient.account_id && (
              <p className="text-xl italic">
                Response times for this seller might take longer than usual
              </p>
            )}
          {userDoc?.roles?.admin && (
            // recipient's phone and email
            <div className="flex flex-col items-start gap-[0.8rem]">
              <h3 className="text-center text-[1.6rem] font-semibold leading-[2.4rem] text-brand-light-black">
                Email: <span className="font-normal">{recipient?.email}</span>
              </h3>
              <h3 className="text-center text-[1.6rem] font-semibold leading-[2.4rem] text-brand-light-black">
                Phone:{' '}
                <span className="font-normal">
                  {formatPhoneNumber(
                    recipient?.phone || recipient?.addresses?.[0]?.phone || ''
                  )}
                </span>
              </h3>
            </div>
          )}
        </div>
        {product && (
          <div className="w-full pl-1">
            {' '}
            <ProductInfo product={product} />{' '}
          </div>
        )}
        {rateLimitError ? (
          <div className="flex flex-col gap-[1.6rem] rounded-[0.8rem] border border-red-200 bg-red-50 p-[1.6rem]">
            <h4 className="text-[1.8rem] font-semibold text-red-800">
              {rateLimitError.title}
            </h4>
            <p className="whitespace-pre-line text-[1.4rem] text-red-700">
              {rateLimitError.message}
            </p>
            <div className="flex gap-[1.2rem]">
              <Button
                type="secondary"
                text="Contact Support"
                href="/contact-us"
                height="small"
              />
              <Button
                type="text"
                text="Dismiss"
                onClick={() => {
                  setRateLimitError(null);
                  dismiss();
                }}
                height="small"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="w-full">
              <FormTextarea
                placeholder="Send message to seller"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <div className="flex items-center justify-center gap-[1.6rem] font-semibold text-brand-secondary">
              <Button
                type="secondary"
                text="Send"
                disabled={isSending}
                onClick={handleSendClick}
              />
            </div>
          </>
        )}
      </div>
    </BaseModal>
  );
}

export default ChatModal;
